/* ==========================================================
   Scent Card — global behavior for any .scent-card on the page
     1. Variant switching via pills / swatches
     2. Quick-add to cart through the theme's <product-form>
        contract (POST /cart/add.js + sections + sections_url,
        then cart.renderContents(response) so the drawer or
        notification updates exactly like the PDP add-to-bag).

   Document-delegated; safe regardless of how many cards are
   on the page or which section rendered them.
   ========================================================== */
(function () {
  'use strict';

  /* ---------- Favorites (localStorage) ----------
     Single source of truth for which products the user has hearted.
     Stored as a JSON array of product handles under one key:

       localStorage["scent_favorites"] = ["nuit-de-bakou","afternoon-swim", ...]

     Exposed on window.ScentFavorites so other surfaces (header badge,
     favorites page) read/write through the same code path. A
     'scent:favorites:change' event fires on document whenever the
     array mutates so open UI can react without polling.

     Handles (not variant ids): a product is favorited as a whole
     concept; switching variants on a card shouldn't change favorite
     state, and the favorites page only needs the handle to fetch
     /products/{handle}.js for rendering. */
  var FAV_KEY = 'scent_favorites';
  function readFavs() {
    try {
      var raw = window.localStorage.getItem(FAV_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(function (h) { return typeof h === 'string' && h; }) : [];
    } catch (_e) { return []; }
  }
  function writeFavs(list) {
    try { window.localStorage.setItem(FAV_KEY, JSON.stringify(list)); }
    catch (_e) {}
    document.dispatchEvent(new CustomEvent('scent:favorites:change', { detail: { list: list.slice() } }));
  }
  function isFav(handle) { return readFavs().indexOf(handle) !== -1; }
  function toggleFav(handle) {
    if (!handle) return false;
    var list = readFavs();
    var i = list.indexOf(handle);
    if (i === -1) { list.push(handle); writeFavs(list); return true; }
    list.splice(i, 1); writeFavs(list); return false;
  }

  window.ScentFavorites = {
    list: readFavs,
    has: isFav,
    add: function (h) { if (h && !isFav(h)) { var l = readFavs(); l.push(h); writeFavs(l); } },
    remove: function (h) { var l = readFavs(); var i = l.indexOf(h); if (i !== -1) { l.splice(i, 1); writeFavs(l); } },
    toggle: toggleFav,
    KEY: FAV_KEY
  };

  /* ---------- Heart button on each card ---------- */
  function syncHeart(btn) {
    var handle = btn.getAttribute('data-product-handle');
    if (!handle) return;
    var on = isFav(handle);
    btn.classList.toggle('is-favorited', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute('aria-label', on ? 'Retirer des favoris' : 'Ajouter aux favoris');
  }
  function syncAllHearts() {
    document.querySelectorAll('[data-favorite-toggle]').forEach(syncHeart);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-favorite-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var handle = btn.getAttribute('data-product-handle');
    if (!handle) return;
    toggleFav(handle);
    btn.classList.remove('is-pulse');
    void btn.offsetWidth;       // restart the CSS pulse animation
    btn.classList.add('is-pulse');
    syncAllHearts();
  });

  // Hydrate on load and re-sync when new cards mount (carousel slides,
  // drawer re-render, recommendations after fetch, etc.).
  document.addEventListener('DOMContentLoaded', syncAllHearts);
  syncAllHearts();
  var heartObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('[data-favorite-toggle]')) syncHeart(node);
        if (node.querySelectorAll) node.querySelectorAll('[data-favorite-toggle]').forEach(syncHeart);
      }
    }
  });
  heartObserver.observe(document.documentElement, { childList: true, subtree: true });

  // Cross-tab sync: another tab toggles → reflect here.
  window.addEventListener('storage', function (e) {
    if (e.key === FAV_KEY) syncAllHearts();
  });
  document.addEventListener('scent:favorites:change', syncAllHearts);

  function findCart() {
    return document.querySelector('cart-notification') ||
           document.querySelector('cart-drawer');
  }

  function getSectionsToRender() {
    var cart = findCart();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      return cart.getSectionsToRender().map(function (s) { return s.id; });
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function flash(el, cls, ms) {
    el.classList.add(cls);
    window.setTimeout(function () { el.classList.remove(cls); }, ms || 1400);
  }

  /* ---------- Whole-card → product URL ----------
     Clicking anywhere on a .scent-card that isn't an interactive
     element (button / link / form control) navigates to the product.
     The cover <a> handles native clicks (right-click, cmd+click,
     keyboard); this handler is the safety net that works even if a
     stacking-context quirk hides the anchor underneath the buy row.
     --------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var card = e.target.closest('.scent-card');
    if (!card) return;
    // Any real interactive element inside the card: leave it alone
    // (cover-link <a>, variant pills/swatches, the + add button, etc.)
    if (e.target.closest('button, a, input, select, textarea, [contenteditable]')) return;

    var url = card.getAttribute('data-product-url');
    if (!url) return;

    var addBtn = card.querySelector('[data-card-add]');
    var variantId = addBtn ? addBtn.getAttribute('data-variant-id') : null;
    var fullUrl = variantId ? url + '?variant=' + variantId : url;

    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
      window.open(fullUrl, '_blank');
    } else {
      window.location.href = fullUrl;
    }
  });

  /* ---------- Variant switching (pills + swatches) ---------- */
  document.addEventListener('click', function (e) {
    var ctrl = e.target.closest('.scent-card__pill, .scent-card__swatch');
    if (!ctrl) return;
    if (ctrl.classList.contains('is-unavailable') || ctrl.disabled) return;

    var card = ctrl.closest('.scent-card');
    if (!card) return;

    var variantId = ctrl.getAttribute('data-variant-id');
    if (!variantId || card.dataset.activeVariant === variantId) return;

    // Active states on controls
    card.querySelectorAll('.scent-card__pill, .scent-card__swatch').forEach(function (el) {
      el.classList.toggle('is-active', el === ctrl);
    });

    // Active states on image pairs
    card.querySelectorAll('.scent-card__img').forEach(function (img) {
      img.classList.toggle('is-active', img.getAttribute('data-variant-id') === variantId);
    });

    // Look up variant payload
    var variants = [];
    try { variants = JSON.parse(card.getAttribute('data-variants') || '[]'); }
    catch (_) {}
    var match = null;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(variantId)) { match = variants[i]; break; }
    }
    if (!match) return;

    // Price (current + optional strikethrough compare-at)
    var nowEl = card.querySelector('[data-card-price-now]');
    var wasEl = card.querySelector('[data-card-price-was]');
    function setCardCompare(value) {
      if (!wasEl) return;
      if (value) {
        wasEl.textContent = value;
        wasEl.hidden = false;
      } else {
        wasEl.textContent = '';
        wasEl.hidden = true;
      }
    }

    // Cover-link href
    var productUrl = card.getAttribute('data-product-url') || '#';
    var coverLink = card.querySelector('[data-card-cover]');
    if (coverLink) coverLink.setAttribute('href', productUrl + '?variant=' + match.id);

    // + button: variant id + availability
    var addBtn = card.querySelector('[data-card-add]');
    var buyRow = card.querySelector('[data-card-buy]');
    if (addBtn) {
      addBtn.setAttribute('data-variant-id', match.id);
      if (match.available) {
        addBtn.disabled = false;
        addBtn.removeAttribute('aria-disabled');
        addBtn.setAttribute('aria-label', 'Ajouter au panier');
        if (nowEl) nowEl.textContent = match.price;
        setCardCompare(match.compare);
      } else {
        addBtn.disabled = true;
        addBtn.setAttribute('aria-disabled', 'true');
        addBtn.setAttribute('aria-label', 'Rupture de stock');
        if (nowEl) nowEl.textContent = 'Rupture';
        setCardCompare(null);
      }
    }
    if (buyRow) buyRow.classList.toggle('is-disabled', !match.available);

    card.dataset.activeVariant = variantId;
  });

  /* ---------- Quick add to cart ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-card-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled || btn.classList.contains('is-loading')) return;

    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    btn.classList.remove('is-success', 'is-error');
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    var cart = findCart();
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('sections', getSectionsToRender().join(','));
    formData.append('sections_url', window.location.pathname);

    var endpoint = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    fetch(endpoint, {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' }
    })
      .then(function (r) { return r.json().then(function (json) { return { ok: r.ok, body: json }; }); })
      .then(function (res) {
        if (!res.ok || res.body.status) {
          flash(btn, 'is-error', 1800);
          return;
        }
        flash(btn, 'is-success', 1400);
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(res.body);
        }
      })
      .catch(function () {
        flash(btn, 'is-error', 1800);
      })
      .finally(function () {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
        // Drop the cart-drawer's is-empty class once an item exists.
        // Mirrors what product-form.js does after a successful add —
        // without this, my .scent-cart.is-empty CSS keeps the new
        // header / items / footer hidden when adding the first item
        // from an empty cart, making the drawer look empty.
        if (cart && cart.classList && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
      });
  });
})();
